import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Badge } from '@ludolio/shared-ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ludolio/shared-ui';
import { Trophy, Users, TrendingUp, EyeOff } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useGameStore } from '@/stores/gameStore';
import { apiClient } from '@/services/api';
import type { AchievementWithUnlockCount, TeacherAchievementOverview } from '@/types';

export function AchievementsView() {
  const { games, fetchGames } = useGameStore();
  const [achievementOverview, setAchievementOverview] = useState<TeacherAchievementOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [overviewResponse] = await Promise.all([
          apiClient.getTeacherAchievementsOverview(),
          fetchGames(),
        ]);

        if (overviewResponse.success && overviewResponse.data) {
          setAchievementOverview(overviewResponse.data);
        } else {
          throw new Error(overviewResponse.error || 'Failed to load achievement overview');
        }
      } catch (error) {
        console.error('Failed to load achievement analytics:', error);
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchGames]);

  const achievements = achievementOverview?.achievements ?? [];
  const totalStudents = achievementOverview?.totalStudents ?? 0;

  const totalAchievements = achievements.length;
  const totalUnlocked = achievementOverview?.totalUnlocked ?? 0;
  const unlockRate = totalStudents > 0 && totalAchievements > 0
    ? ((totalUnlocked / (totalAchievements * totalStudents)) * 100).toFixed(1)
    : '0.0';

  const achievementUnlockData = achievements.map((achievement) => ({
    name: achievement.name,
    unlocks: achievement.unlockCount,
    students: totalStudents,
  }));

  const pieData = [
    { name: 'Unlocked', value: totalUnlocked, color: '#3b82f6' },
    { name: 'Locked', value: Math.max((totalAchievements * totalStudents) - totalUnlocked, 0), color: '#e5e7eb' },
  ];

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Achievements</h1>
        </div>
        <div className="text-center py-8 text-muted-foreground">Loading achievements data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Achievements</h1>
      </div>

      {/* Stats */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Achievements</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAchievements}</div>
            <p className="text-xs text-muted-foreground">Across all games</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Unlocked</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUnlocked}</div>
            <p className="text-xs text-muted-foreground">By all students</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unlock Rate</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unlockRate}%</div>
            <p className="text-xs text-muted-foreground">Overall completion</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="by-game">By Game</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Achievement Unlock Progress</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Number of students who unlocked each achievement</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height={300} minWidth={300}>
                    <BarChart data={achievementUnlockData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={10} />
                      <YAxis fontSize={10} />
                      <Tooltip />
                      <Bar dataKey="unlocks" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Overall Completion</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Total achievements unlocked vs locked</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props: any) => `${props.name} ${(props.percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="by-game" className="space-y-4 mt-6">
          {games.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No games available
              </CardContent>
            </Card>
          ) : (
            games.map((game) => {
              const gameAchievements = achievements.filter(a => a.gameId === game.id);

              return (
                <Card key={game.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{game.title}</CardTitle>
                        <CardDescription>{game.description}</CardDescription>
                      </div>
                      <Badge>{gameAchievements.length} achievements</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {gameAchievements.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        No achievements for this game
                      </div>
                    ) : (
                      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                        {gameAchievements.map((achievement: AchievementWithUnlockCount) => {
                          const unlockCount = achievement.unlockCount;
                          const percentage = totalStudents > 0
                            ? ((unlockCount / totalStudents) * 100).toFixed(0)
                            : '0';

                          return (
                            <div key={achievement.id} className="border rounded-lg p-4 space-y-3">
                              <div className="flex items-start gap-3">
                                <div className="text-2xl">{achievement.icon}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="font-semibold text-sm">{achievement.name}</h4>
                                    {achievement.isSecret && (
                                      <Badge variant="secondary" className="text-xs">
                                        <EyeOff className="w-3 h-3 mr-1" />
                                        Secret
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {achievement.description}
                                  </p>
                                  {achievement.additionalDescription && (
                                    <div className="text-xs text-amber-700 dark:text-amber-400 mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
                                      <div className="font-semibold mb-1">Instructor Notes:</div>
                                      {achievement.additionalDescription}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">
                                    {unlockCount} / {totalStudents} students
                                  </span>
                                  <span className="font-semibold">{percentage}%</span>
                                </div>
                                <div className="w-full bg-secondary rounded-full h-2">
                                  <div
                                    className="bg-primary h-2 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-xs pt-2 border-t">
                                <span className="text-muted-foreground">Points</span>
                                <span className="font-semibold text-yellow-600">{achievement.points}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

